import { useState, useEffect, useCallback, useRef } from 'react'
import { ConversationSummary, ConversationFilters, SortConfig, SortField, SortDirection } from '../types/conversation'
import { PaginationState, ConversationListParams } from '../types/pagination'
import { ConversationListConfig } from '../config/conversationListConfig'
import { adminApi } from '../services/AdminApiService'

export const useConversations = () => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<ConversationFilters>({})
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'lastMessageAt', direction: 'desc' })
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    hasMore: true,
    loading: false
  })
  
  const observerRef = useRef<HTMLDivElement>(null)

  const loadConversations = useCallback(async (reset: boolean = false) => {
    try {
      const isFirstLoad = reset || conversations.length === 0
      if (isFirstLoad) {
        setLoading(true)
        setConversations([])
        setPagination({ page: 1, hasMore: true, loading: false })
      } else {
        setPagination(prev => ({ ...prev, loading: true }))
      }
      
      setError(null)
      
      const params: ConversationListParams = {
        page: isFirstLoad ? 1 : pagination.page,
        limit: ConversationListConfig.itemsPerPage,
        tag: filters.tag && filters.tag !== 'all' ? filters.tag : undefined,
        search: filters.search || undefined
      }
      
      const response = await adminApi.getConversations(params)
      
      if (response.success) {
        const newConversations = response.conversations
        
        if (isFirstLoad) {
          setConversations(newConversations)
        } else {
          setConversations(prev => [...prev, ...newConversations])
        }
        
        setPagination(prev => ({
          page: prev.page + 1,
          hasMore: newConversations.length === ConversationListConfig.itemsPerPage,
          loading: false
        }))
      } else {
        setError('Error al cargar conversaciones')
        setPagination(prev => ({ ...prev, loading: false }))
      }
    } catch (err) {
      setError('Error al cargar conversaciones')
      setPagination(prev => ({ ...prev, loading: false }))
      console.error('Error loading conversations:', err)
    } finally {
      if (conversations.length === 0) {
        setLoading(false)
      }
    }
  }, [filters, pagination.page, conversations.length])

  const loadMoreConversations = useCallback(async () => {
    if (!pagination.hasMore || pagination.loading) return
    await loadConversations(false)
  }, [loadConversations, pagination.hasMore, pagination.loading])

  const loadAvailableTags = useCallback(async () => {
    try {
      const response = await adminApi.getAvailableTags()
      if (response.success) {
        setAvailableTags(response.tags)
      }
    } catch (err) {
      console.error('Error loading tags:', err)
    }
  }, [])

  const sortConversations = useCallback((conversations: ConversationSummary[], config: SortConfig): ConversationSummary[] => {
    return [...conversations].sort((a, b) => {
      let aValue: any = a[config.field]
      let bValue: any = b[config.field]

      if (config.field === 'name') {
        aValue = (a.name || '') + ' ' + (a.lastname || '')
        bValue = (b.name || '') + ' ' + (b.lastname || '')
      }

      if (config.field === 'lastMessageAt') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      }

      if (aValue < bValue) {
        return config.direction === 'asc' ? -1 : 1
      }
      if (aValue > bValue) {
        return config.direction === 'asc' ? 1 : -1
      }
      return 0
    })
  }, [])

  const setupIntersectionObserver = useCallback(() => {
    if (!observerRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !pagination.loading && pagination.hasMore) {
          loadMoreConversations()
        }
      },
      { threshold: ConversationListConfig.observerThreshold }
    )

    observer.observe(observerRef.current)

    return () => observer.disconnect()
  }, [loadMoreConversations, pagination.loading, pagination.hasMore])

  useEffect(() => {
    loadAvailableTags()
  }, [loadAvailableTags])

  useEffect(() => {
    loadConversations(true)
  }, [filters])

  useEffect(() => {
    const cleanup = setupIntersectionObserver()
    return cleanup
  }, [setupIntersectionObserver])

  const applyFilters = useCallback((newFilters: ConversationFilters) => {
    setFilters(newFilters)
  }, [])

  const applySort = useCallback((field: SortField) => {
    const newDirection: SortDirection = 
      sortConfig.field === field && sortConfig.direction === 'desc' ? 'asc' : 'desc'
    
    const newSortConfig: SortConfig = { field, direction: newDirection }
    setSortConfig(newSortConfig)
    
    const sortedConversations = sortConversations(conversations, newSortConfig)
    setConversations(sortedConversations)
  }, [conversations, sortConfig, sortConversations])

  const getConversationStats = useCallback(async (phone: string) => {
    const conversation = conversations.find(c => c.whatsappPhone === phone)
    return conversation || null
  }, [conversations])

  const refreshConversations = useCallback(() => {
    loadConversations(true)
  }, [loadConversations])

  const getTagCount = useCallback((tag: string): number => {
    if (tag === 'all') return conversations.length
    return conversations.filter(c => c.tag === tag).length
  }, [conversations])

  return {
    conversations,
    filteredConversations: conversations,
    loading,
    error,
    filters,
    sortConfig,
    availableTags,
    pagination,
    observerRef,
    applyFilters,
    applySort,
    getConversationStats,
    refreshConversations,
    loadMoreConversations,
    getTagCount,
  }
}
